import { useState, useCallback } from 'react';
import { 
  goatCardsApiService, 
  GoatCard, 
  GoatCardCreateRequest, 
  GoatCardUpdateRequest, 
  GoatCardsSearchFilters,
  GoatCardsApiResponse,
  GoatCardsPaginatedResponse 
} from '../services/goatCardsApiService';

// Hook state interface
interface UseGoatCardsApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// Hook return interface
interface UseGoatCardsApiReturn<T> extends UseGoatCardsApiState<T> {
  execute: (...args: any[]) => Promise<void>;
  reset: () => void;
}

// Generic hook for API operations
function useGoatCardsApi<T>(
  apiFunction: (...args: any[]) => Promise<T>
): UseGoatCardsApiReturn<T> {
  const [state, setState] = useState<UseGoatCardsApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const execute = useCallback(async (...args: any[]) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const result = await apiFunction(...args);
      setState({ data: result, loading: false, error: null });
    } catch (error) {
      setState({ 
        data: null, 
        loading: false, 
        error: error instanceof Error ? error.message : 'An unknown error occurred' 
      });
    }
  }, [apiFunction]);

  const reset = useCallback(() => {
    setState({ data: null, loading: false, error: null });
  }, []);

  return { ...state, execute, reset };
}

// Specific hooks for different operations
export const useGetAllCards = () => {
  return useGoatCardsApi<GoatCardsPaginatedResponse<GoatCard>>(
    (filters?: GoatCardsSearchFilters) => goatCardsApiService.getAllCards(filters)
  );
};

export const useGetCardById = () => {
  return useGoatCardsApi<GoatCardsApiResponse<GoatCard>>(
    (id: string) => goatCardsApiService.getCardById(id)
  );
};

export const useCreateCard = () => {
  return useGoatCardsApi<GoatCardsApiResponse<GoatCard>>(
    (cardData: GoatCardCreateRequest) => goatCardsApiService.createCard(cardData)
  );
};

export const useUpdateCard = () => {
  return useGoatCardsApi<GoatCardsApiResponse<GoatCard>>(
    (cardData: GoatCardUpdateRequest) => goatCardsApiService.updateCard(cardData)
  );
};

export const useDeleteCard = () => {
  return useGoatCardsApi<GoatCardsApiResponse<void>>(
    (id: string) => goatCardsApiService.deleteCard(id)
  );
};

export const useSearchCards = () => {
  return useGoatCardsApi<GoatCardsPaginatedResponse<GoatCard>>(
    (searchTerm: string, filters?: Omit<GoatCardsSearchFilters, 'search'>) => 
      goatCardsApiService.searchCards(searchTerm, filters)
  );
};

export const useSearchEbay = () => {
  return useGoatCardsApi<GoatCardsApiResponse<any>>(
    (searchTerm: string, limit: number = 45, offset: number = 0) => goatCardsApiService.searchEbay(searchTerm, limit, offset)
  );
};

export const useGetHotCards = () => {
  return useGoatCardsApi<GoatCardsApiResponse<GoatCard[]>>(
    (limit: number = 10) => goatCardsApiService.getHotCards(limit)
  );
};

export const useGetCardsByPlayer = () => {
  return useGoatCardsApi<GoatCardsPaginatedResponse<GoatCard>>(
    (player: string, filters?: Omit<GoatCardsSearchFilters, 'player'>) => 
      goatCardsApiService.getCardsByPlayer(player, filters)
  );
};

export const useGetCardsByTeam = () => {
  return useGoatCardsApi<GoatCardsPaginatedResponse<GoatCard>>(
    (team: string, filters?: Omit<GoatCardsSearchFilters, 'team'>) => 
      goatCardsApiService.getCardsByTeam(team, filters)
  );
};

export const useGetCardsBySport = () => {
  return useGoatCardsApi<GoatCardsPaginatedResponse<GoatCard>>(
    (sport: 'basketball' | 'football' | 'baseball', filters?: Omit<GoatCardsSearchFilters, 'sport'>) => 
      goatCardsApiService.getCardsBySport(sport, filters)
  );
};

export const useGetCardStats = () => {
  return useGoatCardsApi<GoatCardsApiResponse<{
    totalCards: number;
    totalValue: number;
    averagePrice: number;
    cardsBySport: { [key: string]: number };
    cardsByYear: { [key: string]: number };
    hotCardsCount: number;
  }>>(() => goatCardsApiService.getCardStats());
};

export const useGetPriceHistory = () => {
  return useGoatCardsApi<GoatCardsApiResponse<{
    cardId: string;
    history: Array<{
      date: string;
      price: number;
      change: number;
    }>;
  }>>((cardId: string, days: number = 30) => goatCardsApiService.getPriceHistory(cardId, days));
};

export const useGetAvailableFilters = () => {
  return useGoatCardsApi<GoatCardsApiResponse<{
    sports: string[];
    teams: string[];
    brands: string[];
    conditions: string[];
    grades: string[];
    gradingCompanies: string[];
    years: number[];
  }>>(() => goatCardsApiService.getAvailableFilters());
};

// Health check hook
export const useHealthCheck = () => {
  return useGoatCardsApi<boolean>(() => goatCardsApiService.healthCheck());
};

// Export the service directly for advanced usage
export { goatCardsApiService }; 